import React, { useContext, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import "../../../assets/css/gcstudent/notesdetail/NoteDetailPage.css";
import AppContext from "../../../context/AppContext";

/* ── Main ── */
const NoteDetailPage = ({ note, onBack }) => {
  const { baseUrl, baseUrlForFiles, authToken, loggedInUser } = useContext(AppContext);

  const [visible,   setVisible]   = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [isSaved,   setIsSaved]   = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  // ── Check if this note is already saved (persists the "Saved!" state across reloads) ──
  useEffect(() => {
    if (!loggedInUser?.id || !authToken) return;

    axios
      .get(`${baseUrl}/student/notes/getSavedNotes/${loggedInUser.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.success === true) {
          const alreadySaved = (res.data.notes || []).some(
            (fav) => fav.lbnote_id === note.id
          );
          setIsSaved(alreadySaved);
        }
      })
      .catch((err) => console.error("Check saved status error:", err));
  }, [baseUrl, authToken, loggedInUser, note.id]);

  // ── Build PDF URL ──
  const pdfUrl = note.pdf_file
    ? `${baseUrlForFiles}/notes/${note.pdf_file}`
    : null;

  // ── Format date ──
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  };

  // ── Save For Later ──
  const handleSaveForLater = () => {
    if (!loggedInUser?.id || !authToken) {
      // Not logged in → save intent → redirect
      localStorage.setItem("pendingNote", JSON.stringify({ noteId: note.id }));
      window.location.href = "/student/login";
      return;
    }

    if (isSaved) return; // already saved

    setSaving(true);

    axios
      .post(
        `${baseUrl}/student/notes/saveNote`,
        {
          lbstudent_id: loggedInUser.id,
          lbnote_id: note.id,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => {
        setSaving(false);
        console.log("Save note response:", res.data);
        if (res.data.success === true) {
          setIsSaved(true);
          Swal.fire({
            icon: "success",
            title: "Saved!",
            text: "You can find it in My Saved Notes.",
            confirmButtonColor: "var(--primary-color)",
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Could Not Save",
            text: res.data.message || "Could not save this note. Please try again.",
            confirmButtonColor: "var(--primary-color)",
          });
        }
      })
      .catch((err) => {
        setSaving(false);
        console.error("Save note error:", err);
        Swal.fire({
          icon: "error",
          title: "Something Went Wrong",
          text: "Please try again.",
          confirmButtonColor: "var(--primary-color)",
        });
      });
  };

  return (
    <div className={`gcStudent-nd-page ${visible ? "gcStudent-nd-visible" : ""}`}>
      <Container fluid="lg">

        {/* ── Back Button ── */}
        <button className="gcStudent-nd-back-btn" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back to Notes
        </button>

        {/* ── Main Card ── */}
        <div className="gcStudent-nd-card">

          {/* ── Header ── */}
          <div className="gcStudent-nd-header">
            <div className="gcStudent-nd-icon-box">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"
                  stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                <path d="M14 2v6h6M9 13h6M9 17h4"
                  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="gcStudent-nd-title-wrap">
              <h1 className="gcStudent-nd-title">{note.title}</h1>
              <p className="gcStudent-nd-subject">
                {note.subject}
                {note.category ? ` • ${note.category}` : ""}
              </p>
            </div>
          </div>

          {/* ── Meta row ── */}
          <div className="gcStudent-nd-meta-row">
            <span className="gcStudent-nd-meta-item">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"
                  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.8"/>
              </svg>
              {note.author_name || note.teacher?.name || "Staff"}
            </span>

            <span className="gcStudent-nd-meta-item">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="4" width="18" height="18" rx="2"
                  stroke="currentColor" strokeWidth="1.8"/>
                <path d="M16 2v4M8 2v4M3 10h18"
                  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
              {formatDate(note.created_at || note.date)}
            </span>

            <span className="gcStudent-nd-meta-item">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M12 15V3M7 10l5 5 5-5"
                  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3 18h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
              {note.downloads || 0} downloads
            </span>
          </div>

          {/* ── Description ── */}
          {note.description && (
            <p className="gcStudent-nd-desc">{note.description}</p>
          )}

          {/* ── PDF Preview ── */}
          <div className="gcStudent-nd-preview">
            {pdfUrl ? (
              <iframe
                src={pdfUrl}
                title={note.title}
                className="gcStudent-nd-pdf-iframe"
                frameBorder="0"
              />
            ) : (
              <div className="gcStudent-nd-preview-inner">
                <svg width="52" height="52" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"
                    stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                  <path d="M14 2v6h6M9 13h6M9 17h4"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <p className="gcStudent-nd-preview-label">PDF not available</p>
              </div>
            )}
          </div>

          {/* ── Buttons ── */}
          <div className="gcStudent-nd-btn-row">

            {/* Download — real link */}
            {pdfUrl ? (
              <a
                href={pdfUrl}
                download={note.title || "note"}
                className="gcStudent-nd-download-btn"
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                  <path d="M12 15V3M7 10l5 5 5-5"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Download Notes
              </a>
            ) : (
              <button className="gcStudent-nd-download-btn" disabled>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                  <path d="M12 15V3M7 10l5 5 5-5"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Download Notes
              </button>
            )}

            {/* Save For Later */}
            <button
              className={`gcStudent-nd-save-btn ${isSaved ? "gcStudent-nd-save-btn--saved" : ""}`}
              onClick={handleSaveForLater}
              disabled={saving || isSaved}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke={isSaved ? "#e05c5c" : "currentColor"}
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                  fill={isSaved ? "#e05c5c" : "none"}/>
              </svg>
              {saving ? "Saving..." : isSaved ? "Saved!" : "Save For Later"}
            </button>

          </div>
        </div>
      </Container>
    </div>
  );
};

export default NoteDetailPage;